import { API_ENDPOINTS } from '../config/api'
import { safeJson } from '../lib/utils'

type ApiResult<T> = { success: boolean; data?: T; message?: string }

async function apiFetch<T>(url: string, token: string, init?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    ...init,
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
      ...(init?.headers || {}),
    },
  })
  const body = (await safeJson(res)) as ApiResult<T> & { message?: string }
  if (!res.ok) throw new Error(body?.message || `Request failed (${res.status})`)
  if (body && typeof body === 'object' && 'success' in body && body.success === false) {
    throw new Error(body.message || 'Request failed')
  }
  return (body?.data ?? body) as T
}

function orgQuery(orgId?: string | null, extra?: Record<string, string | undefined>) {
  const p = new URLSearchParams()
  if (orgId) p.set('organization_id', orgId)
  if (extra) {
    for (const [k, v] of Object.entries(extra)) {
      if (v != null && v !== '') p.set(k, v)
    }
  }
  const s = p.toString()
  return s ? `?${s}` : ''
}

export type TrendSeries = {
  period: string
  from: string
  to: string
  dataPoints: Array<Record<string, unknown>>
  series?: {
    vulnerabilities: Array<{ date: string; value: number }>
    failedControls: Array<{ date: string; value: number }>
    openRisks: Array<{ date: string; value: number }>
    evidenceCoverage: Array<{ date: string; value: number }>
    readiness: Array<{ date: string; status: string }>
  }
  note?: string
}

export async function captureSnapshot(token: string, systemId?: string, orgId?: string | null) {
  return apiFetch<{ id?: string; captured?: number }>(
    `${API_ENDPOINTS.INFORMATION_SYSTEMS_SNAPSHOTS_CAPTURE}${orgQuery(orgId)}`,
    token,
    { method: 'POST', body: JSON.stringify(systemId ? { informationSystemId: systemId } : {}) }
  )
}

export async function fetchTrends(token: string, systemId: string, period: string, orgId?: string | null) {
  return apiFetch<TrendSeries>(
    `${API_ENDPOINTS.INFORMATION_SYSTEM_TRENDS(systemId)}${orgQuery(orgId, { period })}`,
    token
  )
}

export async function fetchOperationalDashboard(token: string, orgId?: string | null) {
  return apiFetch<Record<string, unknown>>(
    `${API_ENDPOINTS.INFORMATION_SYSTEMS_OPERATIONAL_DASHBOARD}${orgQuery(orgId)}`,
    token
  )
}

export async function fetchCorrelations(
  token: string,
  kind: 'vulnerabilities' | 'controls' | 'risks',
  orgId?: string | null
) {
  const url =
    kind === 'vulnerabilities'
      ? API_ENDPOINTS.ASSETS_CORRELATIONS_VULNERABILITIES
      : kind === 'controls'
        ? API_ENDPOINTS.ASSETS_CORRELATIONS_CONTROLS
        : API_ENDPOINTS.ASSETS_CORRELATIONS_RISKS
  return apiFetch<{ correlations: unknown[]; total: number }>(`${url}${orgQuery(orgId)}`, token)
}

export async function linkRiskToSystem(token: string, systemId: string, riskId: string, orgId?: string | null) {
  return apiFetch<unknown>(
    `${API_ENDPOINTS.INFORMATION_SYSTEM_RISK_LINK(systemId, riskId)}${orgQuery(orgId)}`,
    token,
    { method: 'POST' }
  )
}

export async function unlinkRiskFromSystem(token: string, systemId: string, riskId: string, orgId?: string | null) {
  return apiFetch<unknown>(
    `${API_ENDPOINTS.INFORMATION_SYSTEM_RISK_LINK(systemId, riskId)}${orgQuery(orgId)}`,
    token,
    { method: 'DELETE' }
  )
}

export async function updateGovernanceRisk(
  token: string,
  riskId: string,
  payload: Record<string, unknown>
) {
  const res = await fetch(API_ENDPOINTS.GOVERNANCE_RISK(riskId), {
    method: 'PUT',
    headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
  const body = await safeJson(res)
  if (!res.ok) throw new Error((body as { error?: string })?.error || 'Failed to update risk')
  return body
}
